// Orchestration → Signals page.
// All: fleet table of every non-terminal trigger signal.
// Create: pick group + target (thread / all-live) → same wizard.
import { useClient } from '@cortex/react-query';
import type {
  PersistentAgentGroup,
  PersistentAgentListItem,
  PersistentAgentWatch,
} from '@cortex/types';
import { parseCompiledWatchQuery } from '@cortex/types';
import { fork } from '@cortex/utils/fork';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { PageHeader } from '../../../components/common/PageHeader';
import { WatchesPanel } from '../../../components/persistent-agents/WatchesPanel';
import { routes } from '../../../lib/constants';
import { formatRelative } from '../../../lib/format';

export interface IWatchesSearch {
  /** all = fleet table (default); create = wizard launchpad */
  tab?: 'all' | 'create';
  /** Create target helpers */
  groupId?: string;
  threadId?: string;
  agentId?: string;
  /** create scope */
  scope?: 'thread' | 'all-live';
}

export const Route = createFileRoute('/agents/orchestration/signals')({
  validateSearch: (raw: Record<string, unknown>): IWatchesSearch => {
    // Back-compat: old mode=group → create + group scope
    return {
      tab: raw.tab === 'create' ? 'create' : 'all',
      groupId: typeof raw.groupId === 'string' && raw.groupId ? raw.groupId : undefined,
      threadId: typeof raw.threadId === 'string' && raw.threadId ? raw.threadId : undefined,
      agentId: typeof raw.agentId === 'string' && raw.agentId ? raw.agentId : undefined,
      scope: raw.scope === 'all-live' || raw.scope === 'thread' ? raw.scope : undefined,
    };
  },
  component: OrchestrationWatchesPage,
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

const statusBadge: Record<string, string> = {
  STARTING: 'badge badge--warning',
  IDLE: 'badge badge--neutral',
  WORKING: 'badge badge--success',
  PAUSED: 'badge badge--warning',
  WAITING_FOR_INPUT: 'badge badge--warning',
  STOPPED: 'badge badge--default',
  FAILED: 'badge badge--danger',
};

type WatchScope = 'thread' | 'group';

interface IFleetWatchRow {
  key: string;
  watch: PersistentAgentWatch;
  scope: WatchScope;
  groupId: string;
  groupName: string;
  threadId?: string;
  threadName?: string;
  threadStatus?: string;
}

function watchModeLabel(w: PersistentAgentWatch): string {
  if (w.code && parseCompiledWatchQuery(w.code)) {
    return w.condition ? 'list · filter' : 'list · thin';
  }
  if (w.code) return 'map JS';
  if (w.condition) return 'value · gate';
  if (w.selector) return 'value';
  return 'whole payload';
}

function OrchestrationWatchesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const tab = search.tab ?? 'all';

  return (
    <div className="page">
      <PageHeader title="Signals" />
      <p className="t-sm t-muted" style={{ margin: '-8px 0 16px' }}>
        Fleet sensors: MCP pollers that write fact lanes and wake threads. Prefer thread-scoped
        watches for missions and trials.
      </p>

      <div
        role="tablist"
        aria-label="Watches views"
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 'var(--sp-3)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'all'}
          className={tab === 'all' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() =>
            fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'all' } }))
          }
        >
          All watches
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'create'}
          className={tab === 'create' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={() =>
            fork(
              navigate({
                to: routes.ORCHESTRATION_SIGNALS,
                search: {
                  tab: 'create',
                  scope: search.scope ?? 'thread',
                  ...(search.groupId ? { groupId: search.groupId } : {}),
                  ...(search.threadId ? { threadId: search.threadId } : {}),
                  ...(search.agentId ? { agentId: search.agentId } : {}),
                },
              }),
            )
          }
        >
          Create
        </button>
      </div>

      {tab === 'create' ? <CreateWatchFlow /> : <AllWatchesFleet />}
    </div>
  );
}

// ── All watches (fleet) ─────────────────────────────────────────────────────

function AllWatchesFleet() {
  const client = useClient();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'PAUSED' | 'all'>('ACTIVE');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'thread' | 'group'>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [showTerminalThreads, setShowTerminalThreads] = useState(false);

  const groupsQuery = useQuery({
    queryKey: ['persistent-agents'],
    queryFn: () =>
      client.http.get('v1/persistent-agent-groups').json<IListEnvelope<PersistentAgentGroup>>(),
  });
  const groups = useMemo(() => groupsQuery.data?.items ?? [], [groupsQuery.data]);
  const groupNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of groups) m.set(g.id, g.name);
    return m;
  }, [groups]);

  const threads = useQueries({
    queries: groups.map((g) => ({
      queryKey: ['persistent-agents', g.id, 'agents'],
      queryFn: () =>
        client.http
          .get(`v1/persistent-agent-groups/${g.id}/agents`)
          .json<IListEnvelope<PersistentAgentListItem>>(),
      refetchInterval: 8000,
    })),
    combine: (results) => ({
      rows: results.flatMap((r, i) => {
        const gid = groups[i]?.id;
        return (r.data?.items ?? []).map((t) => ({ ...t, _groupId: gid ?? t.agentId }));
      }),
      isLoading: results.some((r) => r.isLoading),
    }),
  });

  const liveThreads = useMemo(
    () =>
      threads.rows.filter(
        (t) => showTerminalThreads || (t.status !== 'STOPPED' && t.status !== 'FAILED'),
      ),
    [threads.rows, showTerminalThreads],
  );

  const threadWatchQueries = useQueries({
    queries: liveThreads.map((t) => ({
      queryKey: ['persistent-agent-watches', t.id],
      queryFn: () =>
        client.http
          .get(`v1/persistent-agent-groups/${t.agentId}/agents/${t.id}/watches`)
          .json<PersistentAgentWatch[]>(),
      refetchInterval: 8000,
    })),
  });

  const fleetRows: IFleetWatchRow[] = useMemo(() => {
    const out: IFleetWatchRow[] = [];
    liveThreads.forEach((t, i) => {
      const list = threadWatchQueries[i]?.data ?? [];
      for (const w of list) {
        out.push({
          key: `t:${w.id}`,
          watch: w,
          scope: 'thread',
          groupId: t.agentId,
          groupName: groupNameById.get(t.agentId) ?? t.agentId,
          threadId: t.id,
          threadName: t.name ?? t.id,
          threadStatus: t.status,
        });
      }
    });
    return out.sort((a, b) => {
      // ACTIVE first
      const as = a.watch.status === 'ACTIVE' ? 0 : 1;
      const bs = b.watch.status === 'ACTIVE' ? 0 : 1;
      if (as !== bs) return as - bs;
      return a.watch.name.localeCompare(b.watch.name);
    });
  }, [liveThreads, threadWatchQueries, groupNameById]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return fleetRows.filter((r) => {
      if (statusFilter !== 'all' && r.watch.status !== statusFilter) return false;
      if (scopeFilter !== 'all' && r.scope !== scopeFilter) return false;
      if (groupFilter !== 'all' && r.groupId !== groupFilter) return false;
      if (!qq) return true;
      const hay = [
        r.watch.name,
        r.watch.toolName,
        r.watch.laneId ?? '',
        r.groupName,
        r.threadName ?? '',
        r.threadId ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(qq);
    });
  }, [fleetRows, q, statusFilter, scopeFilter, groupFilter]);

  const loading =
    groupsQuery.isLoading || threads.isLoading || threadWatchQueries.some((x) => x.isLoading);

  const patchWatch = useMutation({
    mutationFn: async (args: {
      scope: WatchScope;
      groupId: string;
      threadId?: string;
      name: string;
      status: 'ACTIVE' | 'PAUSED';
    }) => {
      return client.http
        .patch(
          `v1/persistent-agent-groups/${args.groupId}/agents/${args.threadId}/watches/${encodeURIComponent(args.name)}`,
          { json: { status: args.status } },
        )
        .json();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['persistent-agent-watches'] });
    },
  });

  const deleteWatch = useMutation({
    mutationFn: async (args: {
      scope: WatchScope;
      groupId: string;
      threadId?: string;
      name: string;
    }) => {
      return client.http
        .delete(
          `v1/persistent-agent-groups/${args.groupId}/agents/${args.threadId}/watches/${encodeURIComponent(args.name)}`,
        )
        .text();
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['persistent-agent-watches'] });
    },
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <input
          className="input"
          style={{ minWidth: 200, flex: '1 1 200px' }}
          placeholder="Search name, tool, lane, thread…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="select-native"
          style={{ width: 140 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
        >
          <option value="ACTIVE">Active</option>
          <option value="PAUSED">Paused</option>
          <option value="all">All statuses</option>
        </select>
        <select
          className="select-native"
          style={{ width: 140 }}
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value as typeof scopeFilter)}
        >
          <option value="all">All scopes</option>
          <option value="thread">Thread</option>
          <option value="group">Group</option>
        </select>
        <select
          className="select-native"
          style={{ width: 180 }}
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
        >
          <option value="all">All groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={showTerminalThreads}
            onChange={(e) => setShowTerminalThreads(e.target.checked)}
          />
          Include stopped threads
        </label>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={() =>
            fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'create' } }))
          }
        >
          Create watch
        </button>
      </div>

      {loading && filtered.length === 0 ? (
        <span className="t-muted t-sm">Loading watches…</span>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 16 }}>
          <p className="t-sm t-muted" style={{ margin: 0 }}>
            No live watches match. Create one to push world changes into fact lanes and wake
            threads.
          </p>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            style={{ marginTop: 10 }}
            onClick={() =>
              fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'create' } }))
            }
          >
            Create watch
          </button>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Group</th>
                <th>Thread</th>
                <th>Scope</th>
                <th>Tool</th>
                <th>Mode</th>
                <th>Lane</th>
                <th>Delivery</th>
                <th>Last poll</th>
                <th>Err</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.key}>
                  <td>
                    <span
                      className={
                        r.watch.status === 'ACTIVE'
                          ? 'badge badge--success'
                          : 'badge badge--warning'
                      }
                    >
                      {r.watch.status}
                    </span>
                  </td>
                  <td>
                    <code>{r.watch.name}</code>
                  </td>
                  <td>{r.groupName}</td>
                  <td>
                    {r.threadId ? (
                      <Link to={routes.orchestrationThread(r.groupId, r.threadId)} className="t-sm">
                        {r.threadName}
                        {r.threadStatus ? (
                          <span
                            className={statusBadge[r.threadStatus] ?? 'badge'}
                            style={{ marginLeft: 6 }}
                          >
                            {r.threadStatus}
                          </span>
                        ) : null}
                      </Link>
                    ) : (
                      <span className="t-muted">—</span>
                    )}
                  </td>
                  <td>{r.scope === 'group' ? 'Group' : 'Thread'}</td>
                  <td>
                    <span className="t-mono t-xs">{r.watch.toolName}</span>
                  </td>
                  <td className="t-xs">{watchModeLabel(r.watch)}</td>
                  <td>
                    <code className="t-xs">{r.watch.laneId ?? '—'}</code>
                  </td>
                  <td className="t-xs">{r.watch.wakeDelivery}</td>
                  <td className="t-xs t-muted">
                    {r.watch.lastPolledAt ? formatRelative(r.watch.lastPolledAt) : 'never'}
                  </td>
                  <td>
                    {r.watch.consecutiveErrors > 0 ? (
                      <span className="badge badge--danger">{r.watch.consecutiveErrors}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td>
                    <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={patchWatch.isPending}
                        onClick={() =>
                          patchWatch.mutate({
                            scope: r.scope,
                            groupId: r.groupId,
                            threadId: r.threadId,
                            name: r.watch.name,
                            status: r.watch.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
                          })
                        }
                      >
                        {r.watch.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                      </button>
                      {r.threadId ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() =>
                            fork(
                              navigate({
                                to: routes.ORCHESTRATION_SIGNALS,
                                search: {
                                  tab: 'create',
                                  scope: 'thread',
                                  groupId: r.groupId,
                                  agentId: r.groupId,
                                  threadId: r.threadId,
                                },
                              }),
                            )
                          }
                        >
                          Edit
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn--destructive btn--sm"
                        disabled={deleteWatch.isPending}
                        onClick={() => {
                          if (!confirm(`Delete watch "${r.watch.name}"?`)) return;
                          deleteWatch.mutate({
                            scope: r.scope,
                            groupId: r.groupId,
                            threadId: r.threadId,
                            name: r.watch.name,
                          });
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="t-xs t-muted" style={{ padding: '8px 12px' }}>
            {filtered.length} watch{filtered.length === 1 ? '' : 'es'}
            {statusFilter === 'ACTIVE' ? ' · showing active only' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Create ──────────────────────────────────────────────────────────────────

function CreateWatchFlow() {
  const client = useClient();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const scope = search.scope ?? 'thread';

  const groupsQuery = useQuery({
    queryKey: ['persistent-agents'],
    queryFn: () =>
      client.http.get('v1/persistent-agent-groups').json<IListEnvelope<PersistentAgentGroup>>(),
  });
  const groups = groupsQuery.data?.items ?? [];

  const groupId = search.groupId ?? search.agentId ?? '';
  const threadId = search.threadId ?? '';

  const threadsQuery = useQuery({
    enabled: !!groupId,
    queryKey: ['persistent-agents', groupId, 'agents'],
    queryFn: () =>
      client.http
        .get(`v1/persistent-agent-groups/${groupId}/agents`)
        .json<IListEnvelope<PersistentAgentListItem>>(),
    refetchInterval: 5000,
  });
  const threads = threadsQuery.data?.items ?? [];
  const liveThreads = threads.filter((t) => t.status !== 'STOPPED' && t.status !== 'FAILED');
  const selectedThread = threads.find((t) => t.id === threadId) ?? null;

  const setSearch = (patch: Partial<IWatchesSearch>) => {
    fork(
      navigate({
        to: routes.ORCHESTRATION_SIGNALS,
        search: {
          tab: 'create',
          scope: patch.scope ?? scope,
          groupId: patch.groupId === undefined ? groupId || undefined : patch.groupId || undefined,
          agentId: patch.groupId === undefined ? groupId || undefined : patch.groupId || undefined,
          threadId:
            patch.threadId === undefined ? threadId || undefined : patch.threadId || undefined,
        },
      }),
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 920 }}>
      <div className="card" style={{ padding: 14 }}>
        <div className="field-label" style={{ marginBottom: 8 }}>
          1. Where does it live?
        </div>
        <div className="pa-watch-mode-grid">
          {(
            [
              ['thread', 'One thread', 'Canonical — sensor on a single live session'],
              [
                'all-live',
                'All live threads in group',
                'Stamp the same watch on every non-stopped session (bench kit)',
              ],
            ] as const
          ).map(([id, label, hint]) => (
            <button
              key={id}
              type="button"
              className={scope === id ? 'pa-watch-mode pa-watch-mode--on' : 'pa-watch-mode'}
              onClick={() =>
                setSearch({
                  scope: id,
                  threadId: id === 'thread' ? threadId : undefined,
                })
              }
            >
              <span className="pa-watch-mode__label">{label}</span>
              <span className="pa-watch-mode__hint">{hint}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 14 }}>
          <label className="field" style={{ flex: '1 1 220px' }}>
            <span className="field-label">Orchestration group</span>
            <select
              className="select-native"
              value={groupId}
              onChange={(e) =>
                setSearch({ groupId: e.target.value, threadId: undefined, agentId: e.target.value })
              }
            >
              <option value="">Choose group…</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>

          {scope === 'thread' && (
            <label className="field" style={{ flex: '1 1 220px' }}>
              <span className="field-label">Thread</span>
              <select
                className="select-native"
                value={threadId}
                disabled={!groupId}
                onChange={(e) => setSearch({ threadId: e.target.value })}
              >
                <option value="">{groupId ? 'Choose live thread…' : 'Pick a group first'}</option>
                {liveThreads.map((t) => (
                  <option key={t.id} value={t.id}>
                    {(t.name ?? t.id).slice(0, 48)} · {t.status}
                  </option>
                ))}
              </select>
            </label>
          )}

          {scope === 'all-live' && groupId && (
            <div className="t-sm t-muted" style={{ alignSelf: 'end', paddingBottom: 8 }}>
              {liveThreads.length} live thread{liveThreads.length === 1 ? '' : 's'} will receive
              this watch when you save.
            </div>
          )}
        </div>
      </div>

      {scope === 'thread' && groupId && threadId ? (
        <div>
          <div className="field-label" style={{ marginBottom: 8 }}>
            2. Configure the watch
          </div>
          <WatchesPanel
            agentId={groupId}
            threadId={threadId}
            status={selectedThread?.status ?? null}
            hideList={true}
            startInCreate={true}
            onSaved={() =>
              fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'all' } }))
            }
          />
        </div>
      ) : scope === 'all-live' && groupId ? (
        <AllLiveCreateStamp groupId={groupId} liveThreads={liveThreads} />
      ) : (
        <p className="t-sm t-muted">
          Select a group{scope === 'thread' ? ' and thread' : ''} to continue.
        </p>
      )}
    </div>
  );
}

/** Create on a template thread, then copy config to every live thread. */
function AllLiveCreateStamp({
  groupId,
  liveThreads,
}: {
  groupId: string;
  liveThreads: PersistentAgentListItem[];
}) {
  const client = useClient();
  const navigate = useNavigate();
  const [templateId, setTemplateId] = useState(liveThreads[0]?.id ?? '');
  const [stampMsg, setStampMsg] = useState<string | null>(null);
  const [stamping, setStamping] = useState(false);

  useEffect(() => {
    if (!templateId && liveThreads[0]) setTemplateId(liveThreads[0].id);
  }, [liveThreads, templateId]);

  const template = liveThreads.find((t) => t.id === templateId) ?? liveThreads[0];

  const stampFromTemplate = async () => {
    if (!template) return;
    setStamping(true);
    setStampMsg(null);
    try {
      const watches = await client.http
        .get(`v1/persistent-agent-groups/${groupId}/agents/${template.id}/watches`)
        .json<PersistentAgentWatch[]>();
      // Use the most recently updated watch as the kit (wizard just saved one).
      const sorted = [...watches].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );
      const src = sorted[0];
      if (!src) {
        setStampMsg('Save a watch on the template thread first (wizard below).');
        return;
      }
      const body = {
        mcpConnectionId: src.mcpConnectionId,
        toolName: src.toolName,
        args: src.args ?? {},
        intervalSec: src.intervalSec,
        laneId: src.laneId ?? src.name,
        wakeDelivery: src.wakeDelivery,
        ...(src.wakePrompt ? { wakePrompt: src.wakePrompt } : {}),
        ...(src.selector ? { selector: src.selector } : {}),
        ...(src.condition ? { condition: src.condition } : {}),
        ...(src.code
          ? {
              code: src.code,
              ...(src.bindings ? { bindings: src.bindings } : {}),
              timeoutMs: src.timeoutMs,
            }
          : {}),
      };
      let ok = 0;
      for (const t of liveThreads) {
        if (t.id === template.id) {
          ok += 1;
          continue;
        }
        await client.http
          .put(
            `v1/persistent-agent-groups/${groupId}/agents/${t.id}/watches/${encodeURIComponent(src.name)}`,
            { json: body },
          )
          .json();
        ok += 1;
      }
      setStampMsg(`Stamped “${src.name}” onto ${ok} live thread(s).`);
      fork(navigate({ to: routes.ORCHESTRATION_SIGNALS, search: { tab: 'all' } }));
    } catch (err) {
      setStampMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setStamping(false);
    }
  };

  if (liveThreads.length === 0) {
    return <p className="t-sm t-muted">No live threads in this group.</p>;
  }

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 12 }}>
        <div className="field-label" style={{ marginBottom: 6 }}>
          2. Build on a template thread, then stamp
        </div>
        <p className="t-xs t-muted" style={{ margin: '0 0 10px' }}>
          Configure the watch once below. Then stamp that config onto every other live thread.
        </p>
        <label className="field" style={{ maxWidth: 360 }}>
          <span className="field-label">Template thread</span>
          <select
            className="select-native"
            value={template?.id ?? ''}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {liveThreads.map((t) => (
              <option key={t.id} value={t.id}>
                {(t.name ?? t.id).slice(0, 48)} · {t.status}
              </option>
            ))}
          </select>
        </label>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            disabled={stamping || !template}
            onClick={() => {
              stampFromTemplate().catch(() => undefined);
            }}
          >
            {stamping ? 'Stamping…' : `Stamp latest watch → ${liveThreads.length} threads`}
          </button>
          {stampMsg && <span className="t-xs t-muted">{stampMsg}</span>}
        </div>
      </div>
      {template && (
        <WatchesPanel
          agentId={groupId}
          threadId={template.id}
          status={template.status}
          hideList={true}
          startInCreate={true}
        />
      )}
    </div>
  );
}

// ── Group mode (legacy fan-out) ─────────────────────────────────────────────
